import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription, catchError, of, takeUntil } from 'rxjs';
import {
  CustomerSubscription,
  CustomerSubscriptionService,
} from '../../services/customer-subscription.service';
import { FamilyMembersService } from '../../services/family-members.service';
import { FamilyStartService } from '../../services/family-start.service';
import { LocationService } from '../../services/location.service';
import { CancellationSettingsService } from '../../services/cancellation-settings.service';
import { PlanChangeSettingsService } from '../../services/plan-change-settings.service';
import { RoleService } from '../../services/role.service';
import {
  SavedCard,
  SubscriptionPaymentMethodService,
} from '../../services/subscription-payment-method.service';
import { ToastService } from '../../services/toast.service';
import {
  FAMILY_PRIMARY_TAG_LABEL,
  FAMILY_TAG_LABEL,
  familyTagsOf,
} from '../../shared/utils/family-tags.util';
import {
  PaymentMethodModalComponent,
  PaymentMethodSubject,
} from '../../shared/components/payment-method-modal/payment-method-modal.component';
import {
  cardWithExpiryLabel,
  renewalCardUpdatedToast,
} from '../../shared/utils/payment-method.copy';
import { vendorLookupLabel } from '../../shared/utils/vendor-membership-lookup.util';
import {
  importedBilledBy,
  offersImportedPaymentMethod,
} from '../../shared/utils/payment-method-access.util';
import { PlanChangeResult, buildPlanChangeNotice } from '../../services/plan-change.service';
import { PlanChangePanelComponent } from '../notification-modal/components/plan-change-panel/plan-change-panel.component';
import { SubscriptionPauseActionsService } from '../../services/subscription-pause-actions.service';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal/confirm-action-modal.component';
import { ManageSubscriptionMenuComponent } from '../../shared/components/manage-subscription-menu/manage-subscription-menu.component';
import { PauseSubscriptionModalComponent } from '../../shared/components/pause-subscription-modal/pause-subscription-modal.component';
import { SubscriptionPauseNoticeComponent } from '../../shared/components/subscription-pause-notice/subscription-pause-notice.component';
import { FamilyMembersModalComponent } from '../../shared/components/family-members-modal/family-members-modal.component';
import { StartFamilyModalComponent } from '../../shared/components/start-family-modal/start-family-modal.component';
import { SubscriptionResultModalComponent } from '../../shared/components/subscription-result-modal/subscription-result-modal.component';
import { SubscriptionStatusTagComponent } from '../../shared/components/subscription-status-tag/subscription-status-tag.component';
import { PauseSubject, SubscriptionCardStatus } from '../../shared/types/subscription-pause.types';
import { PauseFlowController } from '../../shared/utils/pause-flow.controller';
import { FamilyMembersFlowController } from '../../shared/utils/family-members-flow.controller';
import { StartFamilyFlowController } from '../../shared/utils/start-family-flow.controller';
import { activateConfirmCopy, pauseRefusalCopy } from '../../shared/utils/subscription-pause.copy';
import {
  PauseViewState,
  cardStatus as deriveCardStatus,
  pauseViewState,
} from '../../shared/utils/subscription-pause.util';
import { getUserDataLocationId } from '../../shared/utils/auth-headers.util';
import { NotificationOrderRequestHistoryService } from '../notification-modal/services/notification-order-request-history.service';
import {
  buildOrderRequestHistoryInsight,
  formatRewashAvailableUntilTime,
} from '../notification-modal/utils/notification-order-request-history.util';
import { OrderRequestTodayBannerState } from '../notification-modal/models/notification-order-request.models';

/**
 * CWS-1470: Customers → Subscriptions.
 *
 * One card per membership the customer has held, live ones first. Update reuses
 * the CWS-1426 plan-change panel; Pause / Activate now / Cancel scheduled pause
 * (CWS-1547) run through a shared PauseFlowController; Cancel membership (CWS-1780) opens the
 * shared cancel & refund panel keyed by subscription id.
 */
@Component({
  selector: 'app-customer-subscriptions-tab',
  standalone: true,
  imports: [
    CommonModule,
    PlanChangePanelComponent,
    CancelRefundPanelComponent,
    ConfirmActionModalComponent,
    ManageSubscriptionMenuComponent,
    PauseSubscriptionModalComponent,
    PaymentMethodModalComponent,
    FamilyMembersModalComponent,
    StartFamilyModalComponent,
    SubscriptionPauseNoticeComponent,
    SubscriptionResultModalComponent,
    SubscriptionStatusTagComponent,
  ],
  templateUrl: './customer-subscriptions-tab.component.html',
  styleUrls: ['./customer-subscriptions-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CustomerSubscriptionsTabComponent implements OnChanges, OnDestroy {
  @Input() customerId = '';
  /** On every card and the cancel panel's subtitle: whose membership this is. */
  @Input() customerName = '';
  /** The customer's phone, shown beside the plate on every card. */
  @Input() customerPhone = '';
  /** Loaded by the parent so the tab can be hidden before it is ever opened. */
  @Input() subscriptions: CustomerSubscription[] = [];

  /** Hands the refreshed list up so the parent re-evaluates the tab without refetching. */
  @Output() subscriptionsChanged = new EventEmitter<CustomerSubscription[]>();

  isLoading = false;
  loadError = '';

  /** Pause, Activate now and Cancel scheduled pause: modal state, busy and errors. */
  readonly pauseFlow: PauseFlowController;

  /** CWS-1581: Start Family pop-up and its result. */
  readonly startFamilyFlow: StartFamilyFlowController;
  /** The add-members step that follows a started family. */
  readonly membersFlow: FamilyMembersFlowController;

  /** Subscription being re-planned; the panel replaces the list while set. */
  planChangeSubscriptionId: string | null = null;
  planChangeNotice: { title: string; body: string; upcoming: string | null } | null = null;

  /** Tenant switched plan changes on at this location. The API re-checks. */
  planChangeAvailable = false;

  /** CWS-1780: cancel & refund is on at this location. The API re-checks. */
  cancelAvailable = false;
  /** Membership being cancelled; the panel opens over the cards while set. */
  cancelSubscriptionId: string | null = null;
  /** What the last cancellation did, shown above the refreshed cards. */
  cancelNotice: { title: string; body: string } | null = null;
  /** CWS-1536: Payment method's feature flag is on at this location. The API re-checks. */
  paymentMethodAvailable = false;
  /** The company offers it on imported memberships too: a card saved while the vendor bills them. */
  importedPaymentMethodAvailable = false;

  /** Membership whose renewal card is being changed. */
  paymentMethodSubscriptionId: string | null = null;
  /** "Card on file" by membership id; a membership is listed only once its card can be shown. */
  cardsOnFile: Record<string, SavedCard | null> = {};
  /** Memberships whose card svc-order's lookup refused to change (a cash membership, say). */
  cardChangeRefused: Record<string, true> = {};
  /**
   * By imported membership id: whether svc-order can save a card for it (a gate order at a shop
   * with Stripe). False for one with no order: all its payments happen in the vendor's system.
   */
  importedCardsOffered: Record<string, boolean> = {};
  private cardLookups = new Subscription();

  /** The active membership vehicle's most recent visit. Null until it loads. */
  recentPurchase: OrderRequestTodayBannerState | null = null;
  recentRewashLabel = '';

  readonly starValues = [1, 2, 3, 4, 5];

  /** Family plan tag text (CWS-1582 §9). */
  readonly familyTagLabel = FAMILY_TAG_LABEL;
  readonly familyPrimaryLabel = FAMILY_PRIMARY_TAG_LABEL;

  /** Which family tags a membership card carries. */
  familyTags = familyTagsOf;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly customerSubscriptionService: CustomerSubscriptionService,
    private readonly planChangeSettings: PlanChangeSettingsService,
    private readonly cancellationSettings: CancellationSettingsService,
    private readonly locationService: LocationService,
    private readonly historyService: NotificationOrderRequestHistoryService,
    private readonly roleService: RoleService,
    private readonly paymentMethodService: SubscriptionPaymentMethodService,
    private readonly toast: ToastService,
    private readonly familyStart: FamilyStartService,
    familyMembers: FamilyMembersService,
    pauseActions: SubscriptionPauseActionsService
  ) {
    this.pauseFlow = pauseActions.createFlow({
      onDone: () => this.reload(),
      onStale: () => this.reload(),
    });
    this.membersFlow = new FamilyMembersFlowController(familyMembers, {
      onChanged: () => this.reload(),
    });
    this.startFamilyFlow = new StartFamilyFlowController(familyStart, {
      members: this.membersFlow,
      onDone: () => this.reload(),
      onStale: () => this.reload(),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId']) {
      this.closePlanChange();
      this.closeCancel();
      this.paymentMethodSubscriptionId = null;
      this.cardLookups.unsubscribe();
      this.cardsOnFile = {};
      this.cardChangeRefused = {};
      this.importedCardsOffered = {};
      this.planChangeNotice = null;
      this.cancelNotice = null;
      this.loadAvailability();
    }
    if (changes['customerId'] || changes['subscriptions']) {
      this.loadRecentPurchase();
    }
  }

  ngOnDestroy(): void {
    this.pauseFlow.destroy();
    this.startFamilyFlow.destroy();
    this.membersFlow.destroy();
    this.cardLookups.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get locationId(): string | null {
    const id = String(
      this.locationService.getShopLocation()?.id ?? getUserDataLocationId() ?? ''
    ).trim();
    return id || null;
  }

  /** The membership the open plan-change panel belongs to. */
  get planChangeSubscription(): CustomerSubscription | null {
    return this.findSubscription(this.planChangeSubscriptionId);
  }

  /** The membership the open cancel panel belongs to. */
  get cancelSubscription(): CustomerSubscription | null {
    return this.findSubscription(this.cancelSubscriptionId);
  }

  private findSubscription(id: string | null): CustomerSubscription | null {
    return id ? (this.subscriptions.find(s => s.subscription_id === id) ?? null) : null;
  }

  get liveSubscriptions(): CustomerSubscription[] {
    return this.subscriptions.filter(s => s.is_live);
  }

  get pastSubscriptions(): CustomerSubscription[] {
    return this.subscriptions.filter(s => !s.is_live);
  }

  trackBySubscriptionId(_index: number, subscription: CustomerSubscription): string {
    return subscription.subscription_id;
  }

  isStarFilled(banner: OrderRequestTodayBannerState, star: number): boolean {
    const rating = banner.feedbackRating;
    return rating != null && star <= rating;
  }

  /** One live card reads by its state; several read as a plain list. */
  get heading(): string {
    const live = this.liveSubscriptions;
    if (live.length === 1 && deriveCardStatus(live[0]) === 'paused') {
      return 'Paused Subscription';
    }
    return live.length === 1 ? 'Active Subscription' : 'Subscriptions';
  }

  /** The status tag: a pause is derived from `pause.state`, not `display_status`. */
  cardStatus(subscription: CustomerSubscription): SubscriptionCardStatus {
    return deriveCardStatus(subscription);
  }

  /** How this card renders around a pause; the rules live in pauseViewState. */
  viewState(subscription: CustomerSubscription): PauseViewState {
    return pauseViewState(subscription, {
      updateAvailable: this.canUpdate(subscription),
      canManage: this.roleService.canManageCustomers(),
      cancelAvailable: this.canCancel(subscription),
      paymentMethodAvailable: this.canChangePaymentMethod(subscription),
      startFamilyAvailable: this.canStartFamily(subscription),
    });
  }

  pauseSubject(subscription: CustomerSubscription): PauseSubject {
    return {
      subscriptionId: subscription.subscription_id,
      plate: (subscription.license_plate || '').trim(),
      planName: subscription.package_name,
      periodEnd: subscription.current_period_end,
      timeZone: subscription.pause?.time_zone ?? null,
      bankedDays: subscription.pause?.banked_days ?? null,
    };
  }

  /** A refusal inside the Activate now or Cancel scheduled pause confirm. */
  get flowError(): string {
    return pauseRefusalCopy(this.pauseFlow.errorCode);
  }

  get activateMessage(): string {
    return activateConfirmCopy(this.pauseFlow.activateTarget?.bankedDays);
  }

  plate(subscription: CustomerSubscription): string {
    return (subscription.license_plate || '').trim();
  }

  /** "7PLM126 · CA"; the plate alone without a state; "—" when the car has no plate. */
  plateLabel(subscription: CustomerSubscription): string {
    const plate = this.plate(subscription);
    if (!plate) {
      return '\u2014';
    }
    const state = (subscription.vehicle_state || '').trim().toUpperCase();
    return state ? `${plate} \u00b7 ${state}` : plate;
  }

  /** "2022 Porsche 911"; empty when the membership's car is not known. */
  vehicleLabel(subscription: CustomerSubscription): string {
    return [subscription.vehicle_year, subscription.vehicle_make, subscription.vehicle_model]
      .map(part => String(part ?? '').trim())
      .filter(Boolean)
      .join(' ');
  }

  /**
   * A Way membership starts at checkout, so it shows the time; an import's start is the vendor's
   * period start, a date only.
   */
  memberSinceFormat(subscription: CustomerSubscription): string {
    return this.isWay(subscription) ? 'MMM d, y, h:mm a' : 'MMM d, y';
  }

  /** Cancelled at period end: still live and redeemable, never billed again. */
  isCancelling(subscription: CustomerSubscription): boolean {
    return subscription.display_status === 'cancelling';
  }

  /**
   * The next charge; for a cancelling membership, the day it ends. That is the paid period's
   * end, not `next_billing_date`, which can be a pause restart or the renewal that was cancelled.
   */
  periodEndDate(subscription: CustomerSubscription): string | null {
    if (this.isCancelling(subscription)) {
      return subscription.current_period_end || subscription.next_billing_date;
    }
    return subscription.next_billing_date;
  }

  hasPrice(subscription: CustomerSubscription): boolean {
    return subscription.price !== null && subscription.price !== '';
  }

  /** "B2C app" for a Way-app sale, "Way" for one made at the shop, the vendor for an import. */
  sourceLabel(subscription: CustomerSubscription): string {
    if (subscription.is_way_app) {
      return 'B2C app';
    }
    return this.isWay(subscription) ? 'Way' : vendorLookupLabel(subscription.source);
  }

  hasWayPlus(subscription: CustomerSubscription): boolean {
    return subscription.way_plus_status === 'active';
  }

  /** "Active", "Cancelled"..., or "—" when the membership came without Way+. */
  wayPlusLabel(subscription: CustomerSubscription): string {
    const status = (subscription.way_plus_status || '').trim();
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
  }

  washesAvailableLabel(subscription: CustomerSubscription): string {
    if (!subscription.is_live) {
      return '—';
    }
    return subscription.unlimited ? 'Unlimited' : String(subscription.washes_available ?? 0);
  }

  /**
   * Update is offered for a live Way membership when the tenant switched plan
   * changes on at this location. Imported memberships are owned by the vendor.
   */
  canUpdate(subscription: CustomerSubscription): boolean {
    return subscription.can_change_plan && this.planChangeAvailable;
  }

  onUpdate(subscription: CustomerSubscription): void {
    if (!this.canUpdate(subscription)) {
      return;
    }
    this.planChangeNotice = null;
    this.planChangeSubscriptionId = subscription.subscription_id;
  }

  closePlanChange(): void {
    this.planChangeSubscriptionId = null;
  }

  /**
   * CWS-1581: Start Family is offered when the API says so (`can_start_family`). What the card
   * cannot know (a missing phone, another discount) is refused inside the pop-up.
   */
  canStartFamily(subscription: CustomerSubscription): boolean {
    return subscription.can_start_family === true;
  }

  onStartFamily(subscription: CustomerSubscription): void {
    if (!this.canStartFamily(subscription)) {
      return;
    }
    this.startFamilyFlow.open({
      subscriptionId: subscription.subscription_id,
      plate: this.plate(subscription),
      packageName: subscription.package_name,
      state: (subscription.vehicle_state || '').trim().toUpperCase() || null,
    });
  }

  /**
   * CWS-1780: Cancel membership is offered on a live Way membership that is not already
   * cancelling (`can_cancel`, server-derived) when the tenant switched cancel & refund on at
   * this location. No role gate, as with plan changes (CWS-1776); the API re-checks everything.
   */
  canCancel(subscription: CustomerSubscription): boolean {
    return subscription.can_cancel === true && this.cancelAvailable;
  }

  onCancelMembership(subscription: CustomerSubscription): void {
    if (!this.canCancel(subscription)) {
      return;
    }
    this.cancelNotice = null;
    this.cancelSubscriptionId = subscription.subscription_id;
  }

  closeCancel(): void {
    this.cancelSubscriptionId = null;
  }

  /** The cancellation ran: say what happened and refresh so the card reads "Cancelling". */
  onCancelDone(done: CancelRefundDone): void {
    this.cancelNotice = {
      title: done.result.status === 'failed' ? 'Membership not cancelled' : 'Membership cancelled',
      body: done.summary,
    };
    this.cancelSubscriptionId = null;
    this.reload();
  }

  /**
   * CWS-1536: the memberships worth asking svc-order about: a live Way membership that is not
   * cancelling and not a Way-app pass, whenever the kill switch is on.
   */
  private canOfferPaymentMethod(subscription: CustomerSubscription): boolean {
    return (
      this.paymentMethodAvailable &&
      subscription.is_live &&
      subscription.display_status === 'active' &&
      this.isWay(subscription) &&
      !subscription.is_way_app
    );
  }

  /**
   * Payment method is offered on a Way membership as soon as the feature is on, without waiting
   * for svc-order's card lookup (a second or two of Stripe calls), and withdrawn if the lookup
   * refuses it: a cash membership and others the row cannot tell. Opened before the lookup
   * answers, the pop-up loads the cards and explains a refusal itself.
   * An imported membership offers it (a card saved while its vendor bills it) once svc-order says
   * a card can be saved for it, which needs a gate order at a shop with Stripe.
   */
  canChangePaymentMethod(subscription: CustomerSubscription): boolean {
    const id = subscription.subscription_id;
    return (
      (this.canOfferPaymentMethod(subscription) && !this.cardChangeRefused[id]) ||
      this.importedCardsOffered[id] === true
    );
  }

  /** svc-order answered the card lookup for this membership, so its card on file can be shown. */
  private hasCardLookup(subscription: CustomerSubscription): boolean {
    return (
      this.canOfferPaymentMethod(subscription) && subscription.subscription_id in this.cardsOnFile
    );
  }

  private isWay(subscription: CustomerSubscription): boolean {
    return (subscription.source || '').trim().toLowerCase() === 'way';
  }

  onPaymentMethod(subscription: CustomerSubscription): void {
    if (!this.canChangePaymentMethod(subscription)) {
      return;
    }
    this.paymentMethodSubscriptionId = subscription.subscription_id;
  }

  /** What the Payment method pop-up shows for the membership it is open on. */
  get paymentMethodSubject(): PaymentMethodSubject | null {
    const id = this.paymentMethodSubscriptionId;
    const subscription = id ? this.subscriptions.find(s => s.subscription_id === id) : null;
    if (!subscription) {
      return null;
    }
    return {
      subscriptionId: subscription.subscription_id,
      planName: subscription.package_name,
      plate: (subscription.license_plate || '').trim(),
      nextRenewal: subscription.next_billing_date,
      price: subscription.price,
      billedBy: importedBilledBy(subscription.source),
    };
  }

  closePaymentMethod(): void {
    this.paymentMethodSubscriptionId = null;
  }

  /** "Visa •••• 4242", "—" for a membership with no renewal card, null to leave the field out. */
  cardOnFileLabel(subscription: CustomerSubscription): string | null {
    if (!this.hasCardLookup(subscription)) {
      return null;
    }
    const card = this.cardsOnFile[subscription.subscription_id];
    return card ? cardWithExpiryLabel(card) : '—';
  }

  onPaymentMethodUpdated(card: SavedCard): void {
    const subject = this.paymentMethodSubject;
    this.paymentMethodSubscriptionId = null;
    if (subject) {
      // Shown at once; the reload below reads it back from Stripe.
      this.cardsOnFile = { ...this.cardsOnFile, [subject.subscriptionId]: card };
    }
    const toast = renewalCardUpdatedToast(card, subject);
    this.toast.showSuccess(toast.title, toast.detail);
    this.reload();
  }

  /** The change was applied or scheduled: show the notice and refresh the cards. */
  onPlanChangeApplied(result: PlanChangeResult): void {
    const notice = buildPlanChangeNotice(result);
    this.planChangeNotice = notice
      ? { title: notice.title, body: notice.body, upcoming: notice.upcoming }
      : null;
    this.planChangeSubscriptionId = null;
    this.reload();
  }

  /**
   * The design pairs the active membership with that vehicle's latest visit.
   * Read-only: the Rewash action stays on the lane's Active subscription modal,
   * where the attendant has the car in front of them.
   */
  private loadRecentPurchase(): void {
    this.recentPurchase = null;
    this.recentRewashLabel = '';

    const vehicleId = (this.liveSubscriptions[0]?.vehicle_id || '').trim();
    if (!vehicleId) {
      return;
    }
    this.historyService.invalidateForVehicle(vehicleId);
    this.historyService
      .fetchVehicleOrderHistoryPage(vehicleId, 1, true)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe(result => {
        const response = result?.response;
        if (result?.skipped || !response?.status) {
          return;
        }
        const insight = buildOrderRequestHistoryInsight(response, 1, this.historyService.pageLimit);
        this.recentPurchase = insight.banner;
        this.recentRewashLabel = this.buildRewashLabel(response);
      });
  }

  /** "Rewash Available · Until 5:42 PM" when the window on the last wash is still open. */
  private buildRewashLabel(response: Record<string, any>): string {
    const expiresAt =
      response?.['rewash_eligibility']?.expires_at ??
      response?.['membership_rewash_eligibility']?.candidates?.[0]?.expires_at ??
      // CWS-1882: a redeemed prepaid wash has no order, so its window lives here.
      response?.['prepaid_rewash_eligibility']?.candidates?.[0]?.expires_at ??
      null;
    const time = formatRewashAvailableUntilTime(expiresAt);
    return time ? `Rewash Available · Until ${time}` : '';
  }

  reload(): void {
    const customerId = (this.customerId || '').trim();
    if (!customerId) {
      return;
    }
    this.isLoading = true;
    this.loadError = '';
    this.customerSubscriptionService
      .getCustomerSubscriptions(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: subscriptions => {
          this.subscriptions = subscriptions;
          this.isLoading = false;
          this.subscriptionsChanged.emit(subscriptions);
          this.loadCardsOnFile();
        },
        error: () => {
          this.isLoading = false;
          this.loadError = 'Could not refresh subscriptions. Close and reopen the customer.';
        },
      });
  }

  private loadAvailability(): void {
    const locationId = this.locationId;
    this.planChangeAvailable = false;
    this.cancelAvailable = false;
    this.paymentMethodAvailable = false;
    this.importedPaymentMethodAvailable = false;
    if (locationId) {
      this.planChangeSettings
        .getAvailability(locationId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(() => of(null))
        )
        .subscribe(response => {
          this.planChangeAvailable = response?.data?.enabled === true;
        });
      // CWS-1780: cancel & refund is per location, like plan changes.
      this.cancellationSettings
        .getAvailability(locationId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(() => of(null))
        )
        .subscribe(response => {
          this.cancelAvailable = response?.data?.enabled === true;
        });
    }
    // CWS-1536: Payment method does not wait for a shop location; without one the API answers
    // for the session's. Way-billed and imported memberships are set apart per company.
    this.paymentMethodService
      .getAvailability(locationId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe(response => {
        this.paymentMethodAvailable = response?.data?.enabled === true;
        this.importedPaymentMethodAvailable = response?.data?.imported_memberships === true;
        this.loadCardsOnFile();
      });
  }

  /**
   * CWS-1536: "Card on file" for each membership whose card can be changed here, and the
   * memberships svc-order refuses. Runs after the memberships and after availability, whichever
   * answers last; a card already shown stays until its lookup answers, so a reload does not blank
   * it, and a failed lookup changes nothing.
   */
  private loadCardsOnFile(): void {
    this.cardLookups.unsubscribe();
    this.cardLookups = new Subscription();
    this.subscriptions
      .filter(subscription => this.canOfferPaymentMethod(subscription))
      .forEach(subscription => {
        const id = subscription.subscription_id;
        this.cardLookups.add(
          this.paymentMethodService.renewalCardLookup(id).subscribe(lookup => {
            const { [id]: _card, ...otherCards } = this.cardsOnFile;
            const { [id]: _refused, ...otherRefusals } = this.cardChangeRefused;
            if (lookup.eligible) {
              this.cardsOnFile = { ...otherCards, [id]: lookup.card };
              this.cardChangeRefused = otherRefusals;
            } else {
              this.cardsOnFile = otherCards;
              this.cardChangeRefused = { ...otherRefusals, [id]: true };
            }
          })
        );
      });
    this.subscriptions
      .filter(subscription =>
        offersImportedPaymentMethod(subscription, this.importedPaymentMethodAvailable)
      )
      .forEach(subscription => {
        const id = subscription.subscription_id;
        this.cardLookups.add(
          this.paymentMethodService.canSaveImportedCard(id).subscribe(offered => {
            this.importedCardsOffered = { ...this.importedCardsOffered, [id]: offered };
          })
        );
      });
  }
}
